import apolloClient from '@/services/apollo-client';
import { ASSIGN_PHARMACY_MUTATION } from '@/graphql/users';
import { AUTH_USER_QUERY } from '@/graphql/auth-user';
import { removeAccessToken, removeRefreshToken } from '@/utils/token';

const userFromStorage = localStorage.getItem("user");

const state = {
  user: userFromStorage ? JSON.parse(userFromStorage) : null,
};

const mutations = {
  SET_USER(state, user) {
    state.user = user;

    // Check if 'user' is an object. If it is, convert it to a string.
    if (typeof user === "object") {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.setItem("user", user);
    }
  },
  CLEAR_USER(state) {
    state.user = null;
    localStorage.removeItem("user");
  },
};

const actions = {
  setUser({ commit }, user) {
    commit("SET_USER", user);
  },
  clearUser({ commit, dispatch }) {
    commit("CLEAR_USER");
    // Also clear notification stats when user is cleared
    dispatch("notifications/clearNotificationStats", null, { root: true });
  },
  async fetchUser({ commit, state }) {
    try {
      console.log('Fetching user data...');
      
      // Store previous pharmacy assignment for comparison
      const previousUser = state.user;
      const previousPharmacyId = previousUser?.pharmacy_id || previousUser?.pharmacy?.id || null;
      
      const { data } = await apolloClient.query({
        query: AUTH_USER_QUERY,
        fetchPolicy: 'no-cache'
      });
      
      console.log('User data received:', data);
      
      if (data.authUser) {
        const newPharmacyId = data.authUser.pharmacy_id || data.authUser.pharmacy?.id || null;
        const newRole = data.authUser.role;
        
        // Check if pharmacy assignment changed for admin/super admin users
        const isAdmin = newRole === 'ADMIN' || newRole === 'SUPER_ADMIN';
        
        // Only check if current user is admin and pharmacy assignment changed
        // We check previousPharmacyId !== newPharmacyId to catch all changes:
        // - null -> pharmacy_id (assignment)
        // - pharmacy_id -> null (removal)
        // - pharmacy_id_1 -> pharmacy_id_2 (change)
        // Only trigger if we have previous user data (to avoid triggering on initial load)
        if (isAdmin && previousUser && previousPharmacyId !== newPharmacyId) {
          // Pharmacy assignment changed for admin/super admin
          console.warn('⚠️ Pharmacy assignment changed for admin user.');
          console.log('Previous pharmacy ID:', previousPharmacyId);
          console.log('New pharmacy ID:', newPharmacyId);
          
          // Determine the type of change
          const wasAssigned = previousPharmacyId !== null;
          const isAssigned = newPharmacyId !== null;
          const pharmacyChanged = wasAssigned && isAssigned && previousPharmacyId !== newPharmacyId;
          
          // Import i18n and ElNotification dynamically
          const { default: i18n } = await import('@/i18n');
          const { ElNotification } = await import('element-plus');
          const t = i18n.global.t;
          
          // Show appropriate notification with translations
          let notificationMessage = '';
          let notificationType = 'info';
          
          if (!wasAssigned && isAssigned) {
            // Admin was assigned a pharmacy
            notificationMessage = t('pharmacyAssignment.changed.assigned');
            notificationType = 'success';
          } else if (wasAssigned && !isAssigned) {
            // Pharmacy assignment was removed
            notificationMessage = t('pharmacyAssignment.changed.removed');
            notificationType = 'warning';
          } else if (pharmacyChanged) {
            // Pharmacy assignment changed from one to another
            notificationMessage = t('pharmacyAssignment.changed.switched');
            notificationType = 'warning';
          }
          
          ElNotification({
            title: t('pharmacyAssignment.changed.title'),
            message: notificationMessage,
            type: notificationType,
            position: 'bottom-right',
            duration: 5000
          });
          
          // Update user data
          commit("SET_USER", data.authUser);
          
          // Load user's language preference if available
          if (data.authUser.locale) {
            console.log('Loading user language preference:', data.authUser.locale);
            // Import i18n dynamically to avoid circular dependency
            const { default: i18n } = await import('@/i18n');
            i18n.global.locale.value = data.authUser.locale;
            localStorage.setItem('preferred-language', data.authUser.locale);
          }
          
          // Redirect based on the change
          setTimeout(() => {
            if (!isAssigned) {
              // Pharmacy removed - go to admin dashboard
              if (window.location.pathname.startsWith('/pharmacies/') || 
                  window.location.pathname.includes('/pharmacy')) {
                window.location.href = '/dashboard';
              }
            } else if (isAssigned) {
              // Pharmacy assigned or changed - refresh to show pharmacy mode
              // If on admin pages, redirect to pharmacy dashboard
              if (window.location.pathname === '/dashboard' || 
                  window.location.pathname === '/users' ||
                  window.location.pathname.startsWith('/users/')) {
                window.location.href = '/dashboard';
              } else {
                // Otherwise just reload to refresh the UI
                window.location.reload();
              }
            }
          }, 1000);
          
          return data.authUser;
        }
        
        commit("SET_USER", data.authUser);
        console.log('User data stored:', data.authUser);
        
        // Load user's language preference if available
        if (data.authUser.locale) {
          console.log('Loading user language preference:', data.authUser.locale);
          // Import i18n dynamically to avoid circular dependency
          const { default: i18n } = await import('@/i18n');
          i18n.global.locale.value = data.authUser.locale;
          localStorage.setItem('preferred-language', data.authUser.locale);
        }
        
        return data.authUser;
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  },
  logout({ commit, dispatch }) {
    console.log('🔄 Starting logout process from store...');
    
    // Stop notification service polling if it exists
    if (window.notificationService) {
      console.log('🛑 Stopping notification service...');
      window.notificationService.stopListening();
    }
    
    // Stop any token refresh intervals
    if (window.tokenRefreshInterval) {
      console.log('🛑 Clearing token refresh interval...');
      clearTimeout(window.tokenRefreshInterval);
      window.tokenRefreshInterval = null;
    }
    
    // Clear any other global variables
    if (window.testNotification) {
      delete window.testNotification;
    }
    if (window.clearNotificationCache) {
      delete window.clearNotificationCache;
    }
    
    // Clear user from store
    commit("CLEAR_USER");
    
    // Clear notification stats
    dispatch("notifications/clearNotificationStats", null, { root: true });
    
    // Clear tokens
    removeAccessToken();
    removeRefreshToken();
    
    // Clear any other stored data
    localStorage.removeItem('processedNotificationIds');
    
    console.log('✅ Logout cleanup completed, redirecting to login...');
    
    // Use hard redirect to stop all background processes
    window.location.href = "/login";
  },
  async clearPharmacy({ commit, state }) {
    try {
      // Call the GraphQL mutation to clear pharmacy assignment
      const { data } = await apolloClient.mutate({
        mutation: ASSIGN_PHARMACY_MUTATION,
        variables: {
          input: {
            pharmacyId: null
          }
        }
      });

      if (data.assignPharmacy.success) {
        // Update the user in state to remove pharmacy assignment
        const updatedUser = { ...state.user, pharmacy: null };
        commit("SET_USER", updatedUser);
        return data.assignPharmacy;
      } else {
        throw new Error(data.assignPharmacy.message);
      }
    } catch (error) {
      console.error('Error clearing pharmacy assignment:', error);
      throw error;
    }
  },
};

const getters = {
  user: (state) => state.user,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
